import React, { FC, useRef, useContext } from 'react';
import { DatesHeaderProps } from './datesHeader.types';
import { StyledDatesHeaderDiv, StyledDateBoxDiv } from './datesHeader.styles';
import { ContentContext } from '../../index.contexts';
import { formatDateStringBasedOnLocale } from '../../../../../../utils/src/helper';
import { getSearchFormClickTrackingLoc } from '../../../../../../modules/utils/searchFormHelper';

interface DateBoxProps {
  label: string;
  date: string;
  onClearHandler: () => void;
  onClickHandler: () => void;
  focused: boolean;
}

const DateBox: React.FC<DateBoxProps> = ({ label, date, onClearHandler, onClickHandler, focused }) => {
  const { datesPlaceholderText, clear } = useContext(ContentContext);
  const mainBtn = useRef<HTMLButtonElement>(null);
  const formattedDate = formatDateStringBasedOnLocale(date);

  const clearClickHandler = (): void => {
    setTimeout(onClearHandler, 0);
    if (window.navigator?.platform?.indexOf('Win') > -1) {
      mainBtn.current?.setAttribute('aria-live', 'polite');
    }
    mainBtn.current?.focus();
  };

  if (focused) {
    mainBtn.current?.focus();
  }

  return (
    <StyledDateBoxDiv
      data-component-name="a-shop-DatesHeader"
      data-testid="shop-DatesHeader"
      className={focused ? 'shop-button-focus' : ''}
    >
      <button
        type="button"
        ref={mainBtn}
        aria-expanded={focused}
        aria-live={focused ? 'polite' : 'off'}
        className="custom_click_track"
        {...{ custom_click_track_value: `${getSearchFormClickTrackingLoc()}| Date Picker ${label} button |internal` }}
        onClick={onClickHandler}
      >
        <span className="t-label-xs dates-header-label">{label}</span>
        <span className={`h-r-form-field-txt ${!formattedDate ? 'gray-shade' : ''}`}>
          {formattedDate ? formattedDate : datesPlaceholderText}
        </span>
      </button>
      {focused && formattedDate ? (
        <button
          type="button"
          className="link-white icon-cancel cancel-button custom_click_track"
          {...{
            custom_click_track_value: `${getSearchFormClickTrackingLoc()}| Date Picker ${label} clear button |internal`,
          }}
          onClick={clearClickHandler}
        >
          <span className="sr-only">{clear}</span>
        </button>
      ) : (
        ''
      )}
    </StyledDateBoxDiv>
  );
};

const DatesHeader: FC<DatesHeaderProps> = ({
  checkInDate,
  checkOutDate,
  onClearCheckIn,
  onClearCheckOut,
  focusedCheckIn,
  onClickCheckIn,
  onClickCheckOut,
}) => {
  const { checkInLabel, checkOutLabel } = useContext(ContentContext);

  return (
    <StyledDatesHeaderDiv>
      <DateBox
        label={checkInLabel}
        date={checkInDate}
        onClearHandler={onClearCheckIn}
        onClickHandler={onClickCheckIn}
        focused={focusedCheckIn}
      />
      <DateBox
        label={checkOutLabel}
        date={checkOutDate}
        onClearHandler={onClearCheckOut}
        onClickHandler={onClickCheckOut}
        focused={!focusedCheckIn}
      />
    </StyledDatesHeaderDiv>
  );
};

export default DatesHeader;
